// Eskom PARCC — CES working-paper WORKBOOK UPLOAD (R5 #1).
//
// Reviewers receive completed Excel working papers from the consultants and
// previously re-typed them into the CES page — slow and transcription-prone.
// This screen imports a whole workbook in the ImportPage's two-phase shape:
//
//   1. PREVIEW — the file is parsed locally (no writes): each route sheet's
//      evidence/level pairs are read, rows matched to the register by
//      Contract/PO Number, and every row shown with an explicit outcome.
//      Rows whose transaction is ALREADY REVIEWED come back as "replace" and
//      are excluded until individually ticked — an upload persists through the
//      same replace-all capture sequence as a manual save, so overwriting an
//      existing evaluation must be a deliberate act.
//   2. IMPORT — each included row runs the exact manual-capture pipeline:
//      rowVersion guard → buildCesCapture → performCesCapture (four writes +
//      per-transaction audit + backup). A failing row is recorded and the
//      batch CONTINUES. One `ces_upload` batch audit + one cache invalidation.
//
// Permission: the screen is gated by canUploadCesWorkbook (capture roles), but
// the REAL enforcement is per transaction via canEditTransaction inside the
// matcher — a reviewer's upload imports only their own allocations, everything
// else reports "not allocated to you".
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  useToast,
  type Column,
} from '../../components/ui';
import { useTransactions } from '../../hooks';
import { performCesCapture } from '../../hooks/useCaptureCes';
import { useAuth } from '../../lib/auth';
import { checkRowVersion } from '../../lib/concurrency';
import { recordAudit } from '../../lib/dbWrite';
import { canUploadCesWorkbook } from '../../lib/permissions';
import { queryClient } from '../../lib/queryClient';
import { invalidateRegister } from '../../lib/queryKeys';
import { buildCesCapture } from './buildCesCapture';
import { parseCesWorkbook } from './cesUploadParse';
import { matchCesUpload, draftToCaptureInput, type CesUploadDraft } from './matchCesUpload';

const ACCEPT = '.xlsx,.xlsm,.xls';

const OUTCOME_LABELS: Record<CesUploadDraft['outcome'], string> = {
  ready: 'Ready',
  replace: 'Replaces existing capture',
  'not-found': 'Not in the register',
  'not-editable': 'Not allocated to you',
  'no-content': 'No captured content',
  duplicate: 'Duplicate (a later row wins)',
};

interface ResultRow {
  reference: string;
  sheetName: string;
  outcome: 'Saved' | 'Error';
  message: string;
}

export function CesUploadPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { grant, user } = useAuth();
  const { show } = useToast();
  const transactionsQuery = useTransactions();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState('');
  const [ignoredSheets, setIgnoredSheets] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<CesUploadDraft[] | null>(null);
  const [includeReplace, setIncludeReplace] = useState<Set<string>>(new Set());
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState<ResultRow[] | null>(null);

  const allowed = canUploadCesWorkbook(grant);

  const importable = useMemo(
    () =>
      (drafts ?? []).filter(
        (d) =>
          d.outcome === 'ready' ||
          (d.outcome === 'replace' && includeReplace.has(`${d.sheetName}:${d.excelRow}`)),
      ),
    [drafts, includeReplace],
  );
  const replaceCount = useMemo(
    () => (drafts ?? []).filter((d) => d.outcome === 'replace').length,
    [drafts],
  );

  function reset(): void {
    setFileName('');
    setParseError('');
    setIgnoredSheets([]);
    setDrafts(null);
    setIncludeReplace(new Set());
    setResults(null);
    setProgress('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleFile(file: File): Promise<void> {
    reset();
    setFileName(file.name);
    // Matching needs the register — never match against an unfinished fetch
    // (every row would read "Not in the register").
    if (transactionsQuery.isLoading || !transactionsQuery.data) {
      setParseError('The transaction register is still loading — try again in a moment.');
      return;
    }
    try {
      const parsed = parseCesWorkbook(await file.arrayBuffer());
      if (!parsed.sheets.length) {
        throw new Error(
          'No route sheets were recognised in this workbook (expected sheets like "Formal Tendering - Contracts", "LPO", …).',
        );
      }
      setIgnoredSheets(parsed.ignoredSheets);
      const matched = matchCesUpload(parsed, transactionsQuery.data ?? [], grant);
      if (!matched.length) {
        throw new Error('The route sheets contain no populated transaction rows.');
      }
      setDrafts(matched);
    } catch (error) {
      setDrafts(null);
      setParseError(error instanceof Error ? error.message : 'The file could not be read.');
      show('Could not read the working paper. See the message on the page.', 'error');
    }
  }

  function toggleReplace(key: string): void {
    setIncludeReplace((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function runImport(): Promise<void> {
    if (!allowed || !drafts || running) return;
    if (!importable.length) {
      show('No rows are included for import.', 'error');
      return;
    }
    setRunning(true);
    setResults(null);

    const out: ResultRow[] = [];
    let saved = 0;
    for (const [index, draft] of importable.entries()) {
      const tx = draft.tx!;
      setProgress(`Saving ${index + 1} of ${importable.length} — ${tx.referenceNo}…`);
      try {
        // Guard against the PREVIEW-time rowVersion (the CesPage contract):
        // anything that changed the transaction after the preview was matched —
        // another reviewer's capture, an allocation edit — must surface as a
        // conflict here, not be silently replace-all overwritten.
        const guard = await checkRowVersion(tx.referenceNo, tx.rowVersion);
        if (!guard.ok) {
          out.push({
            reference: tx.referenceNo,
            sheetName: draft.sheetName,
            outcome: 'Error',
            message:
              guard.current === null
                ? 'The transaction no longer exists.'
                : 'Changed by someone else during the import — reopen and retry this row.',
          });
          continue;
        }
        const input = draftToCaptureInput(draft, user?.email ?? '');
        if (!input) throw new Error('The row could not be converted into a capture.');
        const payload = buildCesCapture(input);
        await performCesCapture({
          referenceNo: tx.referenceNo,
          transaction: payload.transaction,
          workingPaper: payload.workingPaper,
          cesRows: payload.cesRows,
          findings: payload.findings,
          sourceAction: 'ces_upload',
          auditNote: `Uploaded from "${fileName}" (${draft.sheetName}) — ${payload.cesRows.length} CES row(s), ${payload.findings.length} finding(s)`,
        });
        saved += 1;
        out.push({
          reference: tx.referenceNo,
          sheetName: draft.sheetName,
          outcome: 'Saved',
          message: `${payload.cesRows.length} CES row(s), ${payload.findings.length} finding(s)`,
        });
      } catch (error) {
        out.push({
          reference: tx.referenceNo,
          sheetName: draft.sheetName,
          outcome: 'Error',
          message: error instanceof Error ? error.message : 'The capture failed.',
        });
      }
    }

    const errors = out.length - saved;
    const summary = `CES workbook "${fileName}": ${saved} evaluation(s) saved${errors ? `, ${errors} error(s)` : ''}.`;
    try {
      await recordAudit({ action: 'ces_upload', entityType: 'Transaction', entityId: fileName, note: summary });
    } catch {
      // Best-effort — the per-transaction ces_capture entries already carry the trail.
    }
    invalidateRegister(queryClient);
    setProgress('');
    setResults(out);
    setRunning(false);
    show(summary, errors ? 'error' : 'success');
  }

  if (!allowed) {
    return (
      <Card>
        <h3>Upload completed working papers</h3>
        <p className="small">
          Your role does not capture compliance evaluations, so the workbook upload is not
          available. Ask an administrator if you believe you should have capture access.
        </p>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Card>
    );
  }

  const previewColumns: Column<CesUploadDraft>[] = [
    {
      key: 'include',
      header: 'Import',
      render: (d) => {
        const key = `${d.sheetName}:${d.excelRow}`;
        if (d.outcome === 'ready') return '✓';
        if (d.outcome === 'replace') {
          return (
            <input
              type="checkbox"
              aria-label={`Replace the existing capture for ${d.referenceNo}`}
              checked={includeReplace.has(key)}
              onChange={() => toggleReplace(key)}
            />
          );
        }
        return '—';
      },
    },
    { key: 'reference', header: 'Reference', sortable: true, render: (d) => d.referenceNo || '(blank)' },
    { key: 'sheet', header: 'Sheet', sortable: true, render: (d) => d.sheetName.trim() },
    { key: 'row', header: 'Row', align: 'right', render: (d) => String(d.excelRow) },
    { key: 'division', header: 'Division', render: (d) => d.tx?.division ?? '' },
    {
      key: 'criteria',
      header: 'Captured lines',
      align: 'right',
      render: (d) => (d.draft ? String(d.draft.rows.length) : '0'),
    },
    { key: 'outcome', header: 'Outcome', sortable: true, render: (d) => OUTCOME_LABELS[d.outcome] },
    {
      key: 'warnings',
      header: 'Notes',
      render: (d) => (d.warnings.length ? d.warnings.join(' ') : ''),
    },
  ];

  const resultColumns: Column<ResultRow>[] = [
    { key: 'reference', header: 'Reference', sortable: true, render: (r) => r.reference },
    { key: 'sheet', header: 'Sheet', render: (r) => r.sheetName.trim() },
    { key: 'outcome', header: 'Outcome', sortable: true, render: (r) => r.outcome },
    { key: 'message', header: 'Detail', render: (r) => r.message },
  ];

  return (
    <Card>
      <div className="section-head">
        <div>
          <h3 style={{ margin: 0 }}>Upload completed working papers</h3>
          <div className="small">
            Import a consultants&rsquo; CES workbook (one sheet per route) instead of re-typing each
            evaluation. Rows are matched to the register by Contract/PO Number; you can only import
            transactions you may capture. Importing a row <strong>replaces</strong> that
            transaction&rsquo;s current evaluation and findings, exactly like saving the CES page.
          </div>
        </div>
        <div className="actions">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <div className="form-row" style={{ marginTop: 12 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT}
          aria-label="Choose a completed CES working paper workbook"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
          disabled={running || transactionsQuery.isLoading}
        />
        {fileName && !running && (
          <Button variant="ghost" onClick={reset}>
            Clear
          </Button>
        )}
      </div>

      {parseError && <EmptyState message={parseError} />}
      {ignoredSheets.length > 0 && (
        <p className="small" style={{ marginTop: 8 }}>
          Ignored non-route sheet(s): {ignoredSheets.join(', ')}.
        </p>
      )}

      {drafts && !results && (
        <>
          <div className="section-head" style={{ marginTop: 16 }}>
            <div>
              <h4 style={{ margin: 0 }}>Preview — nothing has been imported yet</h4>
              <div className="small">
                {importable.length} of {drafts.length} row(s) included.
                {replaceCount > 0 &&
                  ` ${replaceCount} row(s) would replace an existing capture — tick each one you intend to overwrite.`}
              </div>
            </div>
            <div className="actions">
              <Button onClick={() => void runImport()} disabled={running || importable.length === 0}>
                {running ? progress || 'Importing…' : `Import ${importable.length} evaluation(s)`}
              </Button>
            </div>
          </div>
          <DataTable
            columns={previewColumns}
            rows={drafts}
            getRowKey={(d) => `${d.sheetName}:${d.excelRow}`}
          />
        </>
      )}

      {results && (
        <>
          <div className="section-head" style={{ marginTop: 16 }}>
            <h4 style={{ margin: 0 }}>Import results</h4>
            <div className="actions">
              <Button variant="ghost" onClick={reset}>
                Upload another workbook
              </Button>
            </div>
          </div>
          <DataTable columns={resultColumns} rows={results} getRowKey={(r) => `${r.sheetName}:${r.reference}`} />
        </>
      )}
    </Card>
  );
}
